import React, { useState, useEffect, useCallback, useRef } from "react";
import "./ReviewsCarousel.css";
import { useSwipeable } from "react-swipeable";
import { useSpring, animated } from "react-spring";
import { ExternalLink } from "lucide-react";
import { reviews as rawReviews } from "../Components/data";

const GOOGLE_MAPS_REVIEWS_URL = "https://www.google.com/maps/place/Square+Computers/@13.4961752,74.7088826,19.5z/data=!4m8!3m7!1s0x3bbc97355b4827b9:0xa700ec0ecc1fb0df!8m2!3d13.4973337!4d74.7100267!9m1!1b1!16s%2Fg%2F11gtzq7f7c?entry=ttu&g_ep=EgoyMDI2MDcyMi4wIKXMDSoASAFQAw%3D%3D";

// data.js exports a double-wrapped array: [ [ {...}, {...} ] ]
const allReviews = (Array.isArray(rawReviews[0]) ? rawReviews[0] : rawReviews).filter(Boolean);

function parseRating(rating) {
    if (typeof rating === 'number') return rating;
    const match = String(rating).match(/(\d)/);
    return match ? parseInt(match[1]) : 5;
}

function getStars(rating) {
    const num = parseRating(rating);
    return '★'.repeat(num) + '☆'.repeat(5 - num);
}

function getAvatarFallback(name) {
    const initial = (name || '?').charAt(0).toUpperCase();
    const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];
    const color = colors[(name || '').charCodeAt(0) % colors.length];
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='56'%3E%3Ccircle cx='28' cy='28' r='28' fill='${color.replace('#','%23')}'/%3E%3Ctext x='28' y='36' text-anchor='middle' fill='white' font-size='24' font-family='sans-serif' font-weight='bold'%3E${initial}%3C/text%3E%3C/svg%3E`;
}

function ReviewsCarousel() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [cardHeight, setCardHeight] = useState(null);
    const cardRefs = useRef([]);
    const [style, api] = useSpring(() => ({
        x: 0,
        config: { tension: 220, friction: 32 },
    }));

    const goTo = useCallback((index) => {
        const next = (index + allReviews.length) % allReviews.length;
        setCurrentIndex(next);
        api.start({ x: -next * 100 });
    }, [api]);

    const measureHeight = useCallback(() => {
        const el = cardRefs.current[currentIndex];
        if (el) setCardHeight(el.offsetHeight);
    }, [currentIndex]);

    // Each card sizes to its own content (see CSS) — only the currently
    // visible one's height drives the wrapper, which animates smoothly
    // between values instead of staying pinned to the tallest review.
    useEffect(() => {
        measureHeight();
    }, [measureHeight]);

    useEffect(() => {
        window.addEventListener('resize', measureHeight);
        return () => window.removeEventListener('resize', measureHeight);
    }, [measureHeight]);

    useEffect(() => {
        const interval = setInterval(() => goTo(currentIndex + 1), 4000);
        return () => clearInterval(interval);
    }, [currentIndex, goTo]);

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => goTo(currentIndex + 1),
        onSwipedRight: () => goTo(currentIndex - 1),
        preventScrollOnSwipe: true,
        trackMouse: true,
    });

    return (
        <div className="reviews-carousel-container">
            <h2 className="reviews-heading">
                What our customers say
                <span className="average-rating">
                    <span className="average-score">5.0</span>{' '}
                    <span className="average-stars">★★★★★</span>{' '}
                    <span className="average-count">· {allReviews.length}+ reviews</span>
                </span>
            </h2>
            <div className="reviews-carousel" {...swipeHandlers}>
                <button
                    className="prev-btn"
                    onClick={() => goTo(currentIndex - 1)}
                    aria-label="Previous review"
                >
                    ‹
                </button>
                <div className="carousel-wrapper" style={{ height: cardHeight ? `${cardHeight}px` : 'auto' }}>
                    <animated.div
                        className="review-cards-wrapper"
                        style={{ transform: style.x.to(x => `translateX(${x}%)`) }}
                    >
                        {allReviews.map((review, index) => (
                            <div className="review-card" key={index} ref={el => { cardRefs.current[index] = el; }}>
                                <img
                                    src={review.profile_picture}
                                    alt={review.name}
                                    referrerPolicy="no-referrer"
                                    onError={(e) => { e.target.src = getAvatarFallback(review.name); }}
                                />
                                <h3>{review.name}</h3>
                                <p className="review-rating">{getStars(review.rating)}</p>
                                {review.review && review.review !== '-' && (
                                    <p className="review-text">{review.review}</p>
                                )}
                                <p className="review-time">{review.time}</p>
                            </div>
                        ))}
                    </animated.div>
                </div>
                <button
                    className="next-btn"
                    onClick={() => goTo(currentIndex + 1)}
                    aria-label="Next review"
                >
                    ›
                </button>
            </div>
            <p className="reviews-disclaimer">
                The data shown above is from Google Maps.{' '}
                <a
                    href={GOOGLE_MAPS_REVIEWS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="reviews-verify-link"
                >
                    Click here to verify <ExternalLink size={12} />
                </a>
            </p>
        </div>
    );
}

export default ReviewsCarousel;
